/* eslint-disable react-hooks/set-state-in-effect */
/* eslint-disable react-hooks/exhaustive-deps */
import { literals } from "@/constants/literals";
import { useEffect, useState } from "react";

import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import useReceptionDataStore from "@/store/useReceptionData.store";
import { useNavigate, useParams } from "react-router-dom";
import type { CapturedPhoto, GroupData, ReceptionStep } from "@/types/types.d";
import { CameraCapture } from "../../components/CameraCapture";
import { BASE_URL } from "@/constants/constants";
import ReceptionOrderHeader from "../components/ReceptionOrderHeader";
import { useDevice } from "@/store/useDevice";
import ReceptionGroupMobileInputs from "../components/ReceptionGroupMobileInputs";
import ReceptionGroupTabletInputs from "../components/ReceptionGroupTabletInputs";

const initial_status = {
  description: "",
  extraInformation: "",
  visibleForClient: true,
  photos: [],
  id: "",
};

const ReceptionEditionGroupPage = () => {
  const { groupId } = useParams() as { groupId: string };
  const [formData, setFormData] = useState<GroupData>(initial_status);
  const [description, setDescription] = useState("");
  const [extraInformation, setExtraInformation] = useState("");
  const { receptionData, receptionGroupData, updateReceptionGroupData } =
    useReceptionDataStore();
  const navigate = useNavigate();
  const { isMobile } = useDevice();

  const handleOnChange = (
    id: string,
    value: string | boolean | CapturedPhoto[],
  ) => {
    setFormData((prevState) => {
      const tmp: any = { ...prevState };
      tmp[id] = value;
      return tmp;
    });
  };

  const changeDescription = (value: string) => {
    setDescription(value);
    if (value === "") {
      handleOnChange("description", "");
      return;
    }
    const entireDescription =
      extraInformation.trim() !== "" ? `${value}: ${extraInformation}` : value;
    handleOnChange("description", entireDescription);
  };

  const changeExtraInformation = (value: string) => {
    setExtraInformation(value);
    if (value.trim() === "") {
      handleOnChange("description", description);
    } else {
      handleOnChange("description", `${description}: ${value}`);
    }
  };

  const isValid = () => {
    if (formData.photos.length === 0) {
      return true;
    }
    if (receptionData.step === literals.inNoServ) {
      if (description === "Others") {
        return extraInformation.trim() !== "";
      }
      return description.trim() !== "";
    }
    return literals.outNoServ;
  };

  const handleOnSubmit = (
    e: React.BaseSyntheticEvent<
      Event,
      EventTarget & HTMLFormElement,
      EventTarget
    >,
  ) => {
    e.preventDefault();
    if (!isValid()) return;
    updateReceptionGroupData({ ...formData, id: Number(groupId) });
    navigate(`${BASE_URL}/capture/reception/resume`);
  };

  useEffect(() => {
    const groupData = receptionGroupData.find(
      (group) => group.id.toString() === groupId,
    );
    if (groupData) {
      setDescription(groupData.description.split(":")[0]);
      setExtraInformation(groupData.description.split(":")[1]?.trim() || "");
      setFormData({
        description: groupData.description,
        visibleForClient: groupData.visibleForClient,
        photos: groupData.photos,
      });
    }
  }, []);

  return (
    <div className="min-h-fulldpx flex flex-col justify-between bg-ib-pt-color-white">
      <ReceptionOrderHeader />
      <form
        onSubmit={handleOnSubmit}
        className="flex flex-col flex-1 justify-between"
      >
        <div className="w-full px-4 md:px-14 pt-4 md:flex-1 md:flex md:flex-col md:justify-between">
          <div className="w-full flex-1">
            <CameraCapture
              onChange={(photos) => handleOnChange("photos", photos)}
              maxPhotos={10}
              photos={formData?.photos || undefined}
            />
          </div>
          {isMobile ? (
            <ReceptionGroupMobileInputs
              step={receptionData.step as ReceptionStep}
              formData={formData}
              description={description}
              extraInformation={extraInformation}
              changeGroupData={handleOnChange}
              changeDescription={changeDescription}
              changeExtraInformation={changeExtraInformation}
            />
          ) : (
            <ReceptionGroupTabletInputs
              step={receptionData.step as ReceptionStep}
              formData={formData}
              description={description}
              extraInformation={extraInformation}
              changeGroupData={handleOnChange}
              changeDescription={changeDescription}
              changeExtraInformation={changeExtraInformation}
            />
          )}
        </div>
        <div className="py-4 flex flex-col pt-4 border-t border-t-ib-pt-color-gray-300 md:flex md:flex-row md:justify-center">
          <div className="px-4 md:capture-button">
            <PrimaryButton
              size="medium"
              label={literals.saveChanges}
              colour="red"
              type="submit"
              onClick={() => {}}
              disabled={!isValid()}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default ReceptionEditionGroupPage;
